import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { buildWhatsAppUrl } from "@/data/translations";
import { MessageCircle } from "lucide-react";

export default function FAQ() {
  const { t } = useLanguage();

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="bg-[#FAFAFA] py-24 md:py-32 dark:bg-[#080808]"
    >
      <div className="mx-auto max-w-4xl px-5 md:px-8">
        <div className="mb-12 text-center md:mb-14">
          <span className="mb-4 inline-block text-xs font-semibold uppercase tracking-[0.3em] text-[#F37021]">
            — {t.faq.eyebrow}
          </span>
          <h2 className="font-display text-4xl font-bold leading-[1.08] text-[#111] sm:text-5xl md:text-6xl dark:text-white">
            {t.faq.title}
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Accordion type="single" collapsible className="w-full rounded-[28px] border border-black/6 bg-white px-5 py-2 shadow-[0_20px_50px_rgba(16,24,40,0.08)] md:px-7 dark:border-white/10 dark:bg-[#121212]" data-testid="faq-accordion">
            {t.faq.items.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`item-${i}`}
                className="border-b border-black/8 last:border-b-0 dark:border-white/10"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="py-6 text-start font-display text-lg font-semibold text-[#111] hover:text-[#F37021] hover:no-underline md:text-xl dark:text-white dark:hover:text-[#F8A056]">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-[15px] leading-relaxed text-[#555] dark:text-[#BAC4D4]">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Extra help card */}
        <div className="mt-14 flex flex-col items-start justify-between gap-5 rounded-[28px] border border-black/6 bg-white p-7 shadow-[0_18px_45px_rgba(16,24,40,0.06)] md:flex-row md:items-center md:p-8 dark:border-white/10 dark:bg-[#121212]">
          <div>
            <h3 className="mb-1 font-display text-xl font-semibold text-[#111] dark:text-white">
              {t.faq.stillQuestions}
            </h3>
            <p className="text-sm text-[#555] dark:text-[#BAC4D4]">
              {t.faq.stillQuestionsSub}
            </p>
          </div>
          <a
            href={buildWhatsAppUrl(t.common.whatsappMsg)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-12 items-center gap-2 rounded-full bg-[#25D366] px-6 font-medium text-white shadow-[0_10px_25px_rgba(37,211,102,0.25)] transition-all hover:-translate-y-0.5 hover:bg-[#1EBE5C]"
            data-testid="faq-whatsapp-cta"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
